import React, { useContext, useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { AppContext } from '@prisjakt/ui-app-context';

import DotsVerticalIcon from 'mdi-react/DotsVerticalIcon';
import ChevronDown from 'mdi-react/ChevronDownIcon';

import Button from '@prisjakt/ui-button';
import Lazy from '@prisjakt/ui-lazy';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';

import Placeholder from './placeholder';
import PriceFilters from './price-filters/price-filters';
import MissingShop from '../missing-shop';
import PriceInformation from '../price-information';
import PriceRow, { columnCount } from '../../../../components/price-row';
import SortOptions from '../sort-options';
import ViewportContext from '../../../../components/viewport/context';

import {
  productPriceClick,
  productClickOutBounce,
} from '../../actions/product';

import { AppState } from '../../../../reducers';

import { getFilter, getValidPriceType } from './utils';

import { trackPriceListFilterUpdate } from '../tracking';

import {
  BadgesFragment,
  Price,
  PriceListFragment,
  ProductPageFragment,
  ProductMediaFragment,
  User,
} from '../../../../types';
import { CurrentCampaign, ProductPriceType } from '../../types';
import { SortOption, SortOptionEnum } from '../sort-options/types';

import {
  usePriceListSmileyRatingExperiment,
  usePriceListExpandedPriceRowExperiment,
} from '../../../../hooks/experiments';

import * as S from './styles';

type OwnProps = {
  id: number;
  adOffset?: number;
  brand?: ProductPageFragment['brand'] | null;
  campaign?: CurrentCampaign | null;
  category?: ProductPageFragment['category'] | null;
  media: ProductMediaFragment['media'] & { first?: string | null };
  prices?: PriceListFragment['prices'];
  productName?: string;
  storeBadges?: BadgesFragment['storeBadges'];
  visiblePrices?: number;
};

type StateProps = {
  me: User;
};

type DispatchProps = {
  dispatchProductPriceClick: (
    id: number,
    price: Price | number,
    featured: boolean,
    status: string,
    isAlternative: boolean
  ) => void;
  dispatchProductClickOutBounce: (id: number, priceId?: string | null) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const ADS_INDEXES = [
  3,
  9,
  15,
  // Ad every 10th price row
  ...Array(15)
    .fill(10)
    .map((v, i) => v + v * i + 15),
];

const PriceList = ({
  id,
  adOffset = 0,
  brand,
  campaign,
  category,
  me,
  media,
  prices,
  productName,
  storeBadges = [],
  visiblePrices = 50,
  dispatchProductPriceClick,
  dispatchProductClickOutBounce,
}: Props) => {
  const { t, market } = useContext(AppContext);
  const { isMobile } = useContext(ViewportContext);
  const [state, setState] = useState({
    showMorePrices: false,
    showInternational: false,
    showUsedProducts: false,
    showNotInMintCondition: false,
    showUnavailable: false,
    showPickupOnly: true,
  });
  const [featuredAlternativePriceType] = useState<string>(
    market.priceList && market.priceList.featuredAlternativePriceType
      ? market.priceList.featuredAlternativePriceType
      : 'international'
  );
  const [hiddenListOptions] = useState<string[]>(
    market.priceList && market.priceList.hiddenListOptions
      ? [...market.priceList.hiddenListOptions]
      : []
  );
  const [reportRowId, setReportRowId] = useState<string | null>(null);
  const [currentSort, setCurrentSort] = useState<SortOption>(
    SortOptionEnum.PRICE
  );

  const advertSpacing = { top: '10px', bottom: '10px' };

  const showPriceListRatingExperiment = usePriceListSmileyRatingExperiment();
  const priceListExpandedPriceRowExperiment = usePriceListExpandedPriceRowExperiment();

  const filterPrices = useCallback(
    p => {
      return p.filter((price: Price) => {
        // Check first if the store's market is allowed
        if (
          !price ||
          !price.store ||
          (price.store.market !== 'primary' && !state.showInternational)
        ) {
          return false;
        }

        // And then check the junk type
        switch (price.type) {
          case 'not_in_mint_condition':
            return state.showNotInMintCondition;
          case 'not_available_for_purchase':
            return state.showUnavailable;
          case 'used_product':
            return state.showUsedProducts;
          case 'pickup_only':
            return state.showPickupOnly;
          default:
            return true;
        }
      });
    },
    [
      state.showInternational,
      state.showNotInMintCondition,
      state.showPickupOnly,
      state.showUnavailable,
      state.showUsedProducts,
    ]
  );

  const [allPrices, setAllPrices] = useState(
    !prices || !prices.nodes ? null : prices.nodes
  ); // All prices
  const [currentPrices, setCurrentPrices] = useState(prices?.nodes || []); // Visible prices, with default max length of n
  const [filteredPrices, setFilteredPrices] = useState([]); // All prices, excluding filtered ones

  const renderPlaceholders = (count: number) => {
    return [...new Array(count)].map((_val, index) => (
      <Placeholder key={`placeholder-${index.toString()}`} />
    ));
  };

  const {
    hasInternationalPrices,
    hasUsedProducts,
    hasNotInMintCondition,
    hasPickupOnly,
    hasUnavailable,
    hasFilter,
  } = getFilter(prices);

  const priceFilters = [
    {
      name: 'showInternational',
      type: ProductPriceType.INTERNATIONAL,
      checked: state.showInternational,
      hasPrices: hasInternationalPrices,
      showAsQuickFilter:
        featuredAlternativePriceType === ProductPriceType.INTERNATIONAL,
      label: t('product', 'price-filter.international'),
      shortLabel: t('product', 'price-filter.international-short'),
      hide: false,
    },
    {
      name: 'showUsedProducts',
      type: ProductPriceType.USED_PRODUCT,
      checked: hasUsedProducts && state.showUsedProducts,
      hasPrices: hasUsedProducts,
      showAsQuickFilter:
        featuredAlternativePriceType === ProductPriceType.USED_PRODUCT,
      label: t('product', 'price-filter.used'),
      hide: false,
    },
    {
      name: 'showNotInMintCondition',
      type: ProductPriceType.NOT_IN_MINT_CONDITION,
      checked: hasNotInMintCondition && state.showNotInMintCondition,
      label: t('product', 'price-filter.not-in-mint-condition'),
      hasPrices: hasNotInMintCondition,
      showAsQuickFilter: false,
      hide: Boolean(
        hiddenListOptions.filter(
          option => option === ProductPriceType.NOT_IN_MINT_CONDITION
        ).length
      ),
    },
    {
      name: 'showPickupOnly',
      type: ProductPriceType.PICKUP_ONLY,
      checked: hasPickupOnly && state.showPickupOnly,
      label: t('product', 'price-filter.pickup-only'),
      hasPrices: hasPickupOnly,
      showAsQuickFilter: false,
      hide: Boolean(
        hiddenListOptions.filter(
          option => option === ProductPriceType.PICKUP_ONLY
        ).length
      ),
    },
    {
      name: 'showUnavailable',
      type: ProductPriceType.UNAVAILABLE,
      checked: hasUnavailable && state.showUnavailable && true,
      label: t('product', 'price-filter.not-available-for-purchase'),
      hasPrices: hasUnavailable,
      showAsQuickFilter: false,
      hide: false,
    },
  ];

  const quickFilters = priceFilters.filter(
    elem => elem.showAsQuickFilter && elem.hasPrices
  );

  const handlePriceFilterCheckbox = useCallback(
    e => {
      const isQuickFilter = Boolean(
        quickFilters.filter(quickFilter => quickFilter.name === e.target.name)
          .length
      );

      const selectedFilter = priceFilters.find(
        priceFilter => priceFilter.name === e.target.name
      );

      if (selectedFilter && selectedFilter.type)
        trackPriceListFilterUpdate(selectedFilter.type, isQuickFilter);

      setState({
        ...state,
        [e.target.name]: e.target.checked,
      });
    },
    [priceFilters, state, quickFilters]
  );

  const setPrices = useCallback(
    p => {
      const allFilteredPrices = filterPrices(p);

      setAllPrices(p);
      setFilteredPrices(allFilteredPrices);
      return state.showMorePrices
        ? setCurrentPrices(allFilteredPrices)
        : setCurrentPrices(allFilteredPrices.slice(0, visiblePrices));
    },
    [filterPrices, state.showMorePrices, visiblePrices]
  );

  const onMorePrices = () => {
    setState({
      ...state,
      showMorePrices: true,
    });
  };

  // Sets prices initially, and re sets prices when a state changes
  useEffect(() => {
    setPrices(allPrices);
  }, [allPrices, setPrices, state]);

  if (allPrices && allPrices.length === 0 && !state.showUsedProducts) {
    // If no prices are visible, include used products
    setState({ ...state, showUsedProducts: true });
  }

  const getMobileAd = (index: number) => {
    const placementNumber = Math.min(3, index + 1);

    const triggerPointProp = placementNumber > 1 ? { triggerPoint: 100 } : {};

    return (
      <S.AdvertTableRowElement key={`mobile-ad-container-${index}`}>
        <td colSpan={columnCount}>
          <S.AdContainer
            placement={`dubbelpanorama${placementNumber}`}
            format={`panorama_${placementNumber}`}
            min={0}
            max={860}
            spacing={advertSpacing}
            {...triggerPointProp}
          />
        </td>
      </S.AdvertTableRowElement>
    );
  };

  const getDesktopAd = (index: number) => {
    const desktopAds = [
      { placement: 'panorama_extra', format: 'panorama_1_wde' },
      {
        placement: 'panorama_extra_2',
        format: 'panorama_2_wde',
        triggerPoint: 100,
      },
      {
        placement: 'panorama_extra_2',
        format: 'panorama_2_wde',
        triggerPoint: 100,
      },
    ].filter(Boolean);
    const adProps = desktopAds[Math.min(desktopAds.length - 1, index)];

    return (
      <S.AdvertTableRowElement
        withPosition
        key={`desktop-ad-container-${index}`}
        className="pj-ui-pricelist--advert"
      >
        <td colSpan={columnCount}>
          <S.AdContainer
            min={860}
            max={10000}
            scalable
            scaleBase=".pj-ui-pricelist--advert"
            spacing={advertSpacing}
            {...adProps}
          />
        </td>
      </S.AdvertTableRowElement>
    );
  };

  const insertTableAds = (tableRows: Price[]) => {
    const adjustedAdsIndexes = ADS_INDEXES.map(
      index => index + adOffset
    ).filter(index => index <= tableRows.length);
    if (!adjustedAdsIndexes.length) {
      // special case for short list
      tableRows.splice(tableRows.length, 0, getDesktopAd(0));
      tableRows.splice(tableRows.length, 0, getMobileAd(0));
      return tableRows;
    }
    for (let i = adjustedAdsIndexes.length - 1; i >= 0; i -= 1) {
      const index = adjustedAdsIndexes[i];

      // Only 3 ads in desktop viewport
      if (i <= 2) {
        tableRows.splice(index, 0, getDesktopAd(i));
      }
      tableRows.splice(index, 0, getMobileAd(i));
    }
    return tableRows;
  };

  const getCampaignStyling = (item: Price) => {
    return campaign &&
      campaign.media &&
      campaign.price &&
      campaign.name &&
      item &&
      item.id === campaign.price.id
      ? { ...campaign.media.colors, name: campaign.name }
      : null;
  };

  const lazyPlaceholderStyle = { height: 57 };

  const lazyPlaceholder = (
    <tr>
      <td colSpan={columnCount} style={lazyPlaceholderStyle} />
    </tr>
  );

  const wrapLazy = (
    order: number,
    rowKey: string,
    component: React.ReactElement
  ) => {
    if (order > 10) {
      return (
        <Lazy placeholder={lazyPlaceholder} offset={200} key={rowKey}>
          {component}
        </Lazy>
      );
    }
    return component;
  };

  const renderPriceRow = (item: Price, index: number) => {
    const sortedStoreBadges = storeBadges
      ? storeBadges
          .filter(badge => badge && badge.itemId)
          .sort((a, b) => b!.nrOfPositiveReviews - a!.nrOfPositiveReviews)
          .reduce((acc, badge) => {
            acc[badge!.itemId!] = acc[badge!.itemId!] || [];
            acc[badge!.itemId!].push(badge);
            return acc;
          }, {})
      : [];

    const rowKey = `${item.id}-${index}`;

    return wrapLazy(
      index,
      rowKey,
      <PriceRow
        key={rowKey}
        name={item.name}
        media={media}
        productId={id}
        productName={productName}
        priceId={item.id}
        priceType={getValidPriceType(item.type)}
        price={item.price}
        deliveryOptions={item.deliveryOptions}
        reportRowId={reportRowId}
        setReportRowId={setReportRowId}
        store={item.store}
        storeInfo={
          item.store && item.store.providedByStore
            ? item.store.providedByStore.generalInformation
            : null
        }
        storeBadges={item.store ? sortedStoreBadges[item.store.id] : []}
        stock={item.stock}
        externalUri={item.externalUri}
        brand={brand}
        alternativePrices={item.alternativePrices}
        openInNewWindow
        brandRecommendedRetailer={Boolean(item.brandRecommendedRetailer)}
        isInternationalPrice={Boolean(
          state.showInternational &&
            item.store &&
            item.store.market === 'secondary'
        )}
        campaignStyling={getCampaignStyling(item)}
        onPriceClick={(
          featured: boolean,
          price: number | undefined,
          status: string,
          isAlternative: boolean
        ) => {
          // NOTE: Any changes done to data or structure of PRODUCT_PRICE_CLICK needs to be
          // reflected in react/middlewares/out pre click for tracking clicks before JS bundle
          dispatchProductPriceClick(
            id,
            price || item,
            featured,
            status,
            isAlternative
          );
        }}
        onClickOutBounce={dispatchProductClickOutBounce}
        currentSort={currentSort}
        showPriceListRatingExperiment={showPriceListRatingExperiment}
        showPriceListExpandedPriceRowExperiment={
          priceListExpandedPriceRowExperiment.show
        }
      />
    );
  };

  return (
    <Panel
      header={
        <S.Header>
          <PanelHeader
            title={
              <>
                <S.OnlyDesktop data-test="SortOrder">
                  {t('product', 'prices.title')}
                  <S.TitleCount>({filteredPrices.length})</S.TitleCount>
                  {me?.analyticsAccess?.stores && <MissingShop />}
                </S.OnlyDesktop>
              </>
            }
            divider="none"
          >
            {allPrices && (
              <SortOptions
                prices={allPrices as Price[]}
                setPrices={setPrices}
                compact={isMobile && Boolean(quickFilters.length)}
                setCurrentSort={setCurrentSort}
                currentSort={currentSort}
              />
            )}
            <S.QuickPriceFilters
              priceFilters={quickFilters}
              onFilterChanged={handlePriceFilterCheckbox}
            />

            {hasFilter && (
              <S.PriceFilter
                title={
                  <>
                    <S.OnlyMobile>
                      <DotsVerticalIcon size={20} />{' '}
                    </S.OnlyMobile>
                    <S.TabletDesktop>
                      {t('product', 'prices.more-filters')}{' '}
                    </S.TabletDesktop>
                  </>
                }
                type="custom-content-type"
                headerPosition="right"
                ariaLabel={t('product', 'prices.more-filters')}
                data-test="PriceFilters"
              >
                <PriceFilters
                  priceFilters={priceFilters}
                  onFilterChanged={handlePriceFilterCheckbox}
                />
              </S.PriceFilter>
            )}
          </PanelHeader>
        </S.Header>
      }
      footer={
        <div>
          {market.showProductInformationTexts &&
            currentPrices &&
            currentPrices.length > 0 && (
              <S.Information>
                {t('product', 'price-list.data-information')}
              </S.Information>
            )}
          {currentPrices &&
            allPrices &&
            currentPrices.length < filterPrices(allPrices).length && (
              <>
                <Button
                  style={{ width: '100%' }}
                  iconPosition="bottom"
                  icon={<ChevronDown />}
                  variant="text"
                  color="link"
                  onClick={() => onMorePrices()}
                >
                  {t('product', 'prices.show-more-prices-button')}
                </Button>
              </>
            )}
        </div>
      }
    >
      {prices && prices.meta && prices.meta.storeStatistics && (
        <PriceInformation storeStatistics={prices.meta.storeStatistics} />
      )}

      {currentPrices === null && renderPlaceholders(5)}
      {currentPrices && currentPrices.length === 0 && (
        <S.NoPrices key="no-prices">
          <div>
            {t('product', 'prices.no-prices')}
            {category && (
              <div>
                {t('product', 'prices.view-all-in-category-link')}{' '}
                <Link to={category.pathName}>{category.name}</Link>
              </div>
            )}
          </div>
        </S.NoPrices>
      )}
      <S.PriceRowTable>
        <thead>
          <tr>
            <S.PriceRowTableHeadExpand />
            <S.PriceRowTableHeadStore scope="col">
              <div>{t('product', 'price-list.header-store')}</div>
            </S.PriceRowTableHeadStore>
            <S.PriceRowTableHeadProduct scope="col">
              <div>{t('product', 'price-list.header-product')}</div>
            </S.PriceRowTableHeadProduct>
            <S.PriceRowTableHeadStock scope="col">
              <div>{t('product', 'price-list.header-stock')}</div>
            </S.PriceRowTableHeadStock>
            <S.PriceRowTableHeadPrice scope="col">
              <div>{t('product', 'price-list.header-price')}</div>
            </S.PriceRowTableHeadPrice>
            <S.PriceRowTableHeadClickOut />
          </tr>
        </thead>
        <tbody>
          {currentPrices &&
            currentPrices.length > 0 &&
            insertTableAds(
              currentPrices.map((item, index) =>
                renderPriceRow(item as Price, index)
              )
            )}
        </tbody>
      </S.PriceRowTable>
    </Panel>
  );
};

const mapStateToProps = (state: AppState) => ({
  me: state.areas.account.me,
});

const dispatchProps = {
  dispatchProductPriceClick: productPriceClick,
  dispatchProductClickOutBounce: productClickOutBounce,
};

export default connect(mapStateToProps, dispatchProps)(PriceList);
